const _base = (process.env.REACT_APP_API_URL || 'http://localhost:8080').replace(/\/$/, '');
const API_BASE = _base.endsWith('/api') ? _base.replace(/\/api$/, '') : _base;

async function fetchWithAuth(url, options = {}) {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  if (token && token.trim()) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.dispatchEvent(new CustomEvent('auth:unauthorized'));
  }
  return res;
}

export async function login(email, password) {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    throw new Error(data.error || 'Erro ao fazer login');
  }
  if (!data.token) throw new Error('Resposta do login inválida: token não recebido');
  return data;
}

export async function getCurrentUser() {
  const res = await fetchWithAuth(`${API_BASE}/auth/me`);
  if (!res.ok) return null;
  return res.json();
}

export async function register(name, email, password, organizationId) {
  const res = await fetch(`${API_BASE}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, email, password, organizationId }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Erro ao registrar');
  return data;
}

export async function createOrganization(data) {
  const res = await fetchWithAuth(`${API_BASE}/organizations`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Erro ao criar organização');
  return json;
}

export async function updateOrganization(id, data) {
  const res = await fetchWithAuth(`${API_BASE}/organizations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Erro ao atualizar organização');
  return json;
}

export async function getOrganization(id) {
  const res = await fetchWithAuth(`${API_BASE}/organizations/${id}`);
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Erro ao buscar organização');
  return json;
}

export async function getOrganizationUsers(organizationId) {
  const res = await fetchWithAuth(`${API_BASE}/organizations/${organizationId}/users`);
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Erro ao buscar usuários');
  return json;
}

export async function updateUserBypassPermission(userId, enabled) {
  const res = await fetchWithAuth(`${API_BASE}/users/${userId}/bypass-phishing-verification`, {
    method: 'PATCH',
    body: JSON.stringify({ enabled }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Erro ao atualizar permissão');
  return json;
}

export async function getQuestions() {
  const res = await fetchWithAuth(`${API_BASE}/questions`);
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Erro ao buscar perguntas');
  return json;
}

export async function submitAssessment(organizationId, answers) {
  const res = await fetchWithAuth(`${API_BASE}/organizations/${organizationId}/assessments`, {
    method: 'POST',
    body: JSON.stringify({ answers }),
  });
  const text = await res.text();
  const json = text ? JSON.parse(text) : {};
  if (!res.ok) throw new Error(json.error || 'Erro ao enviar avaliação');
  if (!json.id) throw new Error('Resposta inválida do servidor. Tente novamente.');
  return json;
}

export async function getAssessments(organizationId) {
  const res = await fetchWithAuth(`${API_BASE}/organizations/${organizationId}/assessments`);
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Erro ao buscar avaliações');
  return json;
}

export async function getAssessmentReport(organizationId, id) {
  const res = await fetchWithAuth(`${API_BASE}/organizations/${organizationId}/assessments/${id}`);
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Erro ao buscar relatório');
  return json;
}

export async function executeSimulation(organizationId, type) {
  const res = await fetchWithAuth(
    `${API_BASE}/organizations/${organizationId}/simulations?type=${type}`,
    { method: 'POST' }
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Erro ao executar simulação');
  return json;
}

export async function executeBruteForceLab(organizationId, metrics) {
  const res = await fetchWithAuth(
    `${API_BASE}/organizations/${organizationId}/simulations/brute-force-lab`,
    {
      method: 'POST',
      body: JSON.stringify(metrics),
    }
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Erro ao executar laboratório de força bruta');
  return json;
}

export async function getSimulations(organizationId) {
  const res = await fetchWithAuth(`${API_BASE}/organizations/${organizationId}/simulations`);
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Erro ao buscar simulações');
  return json;
}

export async function getSimulation(organizationId, id) {
  const res = await fetchWithAuth(`${API_BASE}/organizations/${organizationId}/simulations/${id}`);
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Erro ao buscar simulação');
  return json;
}

export async function getDashboard(organizationId) {
  const res = await fetchWithAuth(`${API_BASE}/organizations/${organizationId}/dashboard`);
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Erro ao buscar dashboard');
  return json;
}

export async function sendPhishingVerificationCode(email, authorized) {
  const url = `${API_BASE}/phishing-tests/send-verification-code`;
  const res = await fetchWithAuth(url, {
    method: 'POST',
    body: JSON.stringify({ email, authorized }),
  });
  const text = await res.text();
  let json = {};
  if (text) {
    try {
      json = JSON.parse(text);
    } catch (_) { /* ignore */ }
  }
  if (!res.ok) {
    throw new Error(json.error || json.message || 'Erro ao enviar código de verificação');
  }
  return;
}

export async function verifyPhishingCode(email, code) {
  const url = `${API_BASE}/phishing-tests/verify-code`;
  const res = await fetchWithAuth(url, {
    method: 'POST',
    body: JSON.stringify({ email, code }),
  });
  const text = await res.text();
  let json = {};
  if (text) {
    try {
      json = JSON.parse(text);
    } catch (_) { /* ignore */ }
  }
  if (!res.ok) {
    throw new Error(json.error || json.message || 'Código inválido ou expirado');
  }
  return json;
}

export async function startPhishingTest(email, authorized, verificationToken = null, skipVerification = false) {
  const url = `${API_BASE}/phishing-tests/start`;
  const body = { email, authorized };
  if (verificationToken) body.verificationToken = verificationToken;
  if (skipVerification) body.skipVerification = true;
  const res = await fetchWithAuth(url, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  const contentType = res.headers.get('content-type') || '';
  const text = await res.text();
  let json = {};
  if (contentType.includes('application/json') && text) {
    try {
      json = JSON.parse(text);
    } catch (_) { /* ignore */ }
  }
  if (!res.ok) {
    throw new Error(json.error || (res.status === 404 ? 'Endpoint não encontrado. Verifique se a API está em execução.' : 'Erro ao iniciar simulação'));
  }
  return json;
}

export async function recordPhishingClick(token) {
  const res = await fetch(`${API_BASE}/phishing-tests/${token}/record-click`);
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Link inválido ou expirado');
  return json;
}

export async function getSocialEngineeringScenarios(organizationId) {
  const res = await fetchWithAuth(
    `${API_BASE}/organizations/${organizationId}/social-engineering/scenarios`
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Erro ao buscar cenários');
  return json;
}

export async function submitSocialEngineeringResponse(organizationId, scenarioCode, selectedOptionIndex) {
  const res = await fetchWithAuth(
    `${API_BASE}/organizations/${organizationId}/social-engineering/submit`,
    {
      method: 'POST',
      body: JSON.stringify({ scenarioCode, selectedOptionIndex }),
    }
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Erro ao enviar resposta');
  return json;
}

export async function getChatScenarios(organizationId) {
  const res = await fetchWithAuth(
    `${API_BASE}/organizations/${organizationId}/social-engineering-chat/scenarios`
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Erro ao buscar cenários');
  return json;
}

export async function startChatSimulation(organizationId, scenarioCode) {
  const res = await fetchWithAuth(
    `${API_BASE}/organizations/${organizationId}/social-engineering-chat/start?scenarioCode=${encodeURIComponent(scenarioCode)}`,
    { method: 'POST' }
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Erro ao iniciar chat');
  return json;
}

export async function submitChatResponse(organizationId, attemptId, stepIndex, selectedOptionIndex) {
  const res = await fetchWithAuth(
    `${API_BASE}/organizations/${organizationId}/social-engineering-chat/submit`,
    {
      method: 'POST',
      body: JSON.stringify({ attemptId, stepIndex, selectedOptionIndex }),
    }
  );
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Erro ao enviar resposta');
  return json;
}
